import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Atom, Download, FileSpreadsheet, FileText, Loader2, ShieldAlert, Eye,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

async function downloadBlob(url, fallbackName) {
  const res = await api.get(url, { responseType: 'blob' });
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^";]+)"?/);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(res.data);
  a.download = (m && m[1]) || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

function ComputedTable({ title, rows }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{title}</h4>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground border-b border-border">
            <th className="text-left py-1 pr-2">Constructo</th>
            <th className="text-right py-1 px-2">Base</th>
            <th className="text-right py-1 px-2">Presión</th>
            <th className="text-right py-1 pl-2">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-b border-border/50">
              <td className="py-1 pr-2 font-medium">{r.name_es || r.name}</td>
              <td className="py-1 px-2 text-right tabular-nums">{r.base ?? '—'}</td>
              <td className="py-1 px-2 text-right tabular-nums">{r.pressure ?? '—'}</td>
              <td className={`py-1 pl-2 text-right tabular-nums font-semibold ${r.delta > 0 ? 'text-orange-600' : r.delta < 0 ? 'text-sky-600' : ''}`}>
                {r.delta != null ? (r.delta > 0 ? `+${r.delta}` : r.delta) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminVectorV8() {
  const { t } = useTranslation();
  const [questionnaires, setQuestionnaires] = useState(null);
  const [selectedQ, setSelectedQ] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loadingAsn, setLoadingAsn] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [computed, setComputed] = useState(null);
  const [computedOpen, setComputedOpen] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api.get('/admin/vector-v8/questionnaires')
      .then((r) => {
        setQuestionnaires(r.data.items || []);
        if ((r.data.items || []).length === 1) setSelectedQ(r.data.items[0].questionnaire_id);
      })
      .catch(() => setQuestionnaires([]));
  }, []);

  useEffect(() => {
    if (!selectedQ) { setAssignments([]); return; }
    setLoadingAsn(true);
    const params = new URLSearchParams({ questionnaire_id: selectedQ, status: 'submitted' });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    api.get(`/admin/vector-v8/assignments?${params}`)
      .then((r) => setAssignments(r.data.items || []))
      .catch(() => setAssignments([]))
      .finally(() => setLoadingAsn(false));
  }, [selectedQ, dateFrom, dateTo]);

  const current = useMemo(
    () => (questionnaires || []).find((q) => q.questionnaire_id === selectedQ),
    [questionnaires, selectedQ]);
  const val = current?.validation;

  const doDownload = async (key, url, name) => {
    setBusy(key);
    try { await downloadBlob(url, name); }
    catch { toast.error('Export failed'); }
    finally { setBusy(''); }
  };

  const openComputed = async (aid) => {
    setBusy(`comp-${aid}`);
    try {
      const { data } = await api.get(`/admin/vector-v8/assignments/${aid}/computed`);
      setComputed(data);
      setComputedOpen(true);
    } catch { toast.error('Could not load computed results'); }
    finally { setBusy(''); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="admin-vector-v8-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Atom className="h-6 w-6 text-primary" /> Vector V8
            <Badge variant="outline" className="text-xs" data-testid="vector-version-badge">V1.1</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('vectorv8.subtitle', { defaultValue: 'Administración, exportación de respuestas crudas y cálculo determinístico (Base / Presión / Δ / SAT / IDP). La interpretación se realiza externamente.' })}
          </p>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2" data-testid="vector-pilot-banner">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          {t('vectorv8.pilotBanner', { defaultValue: 'Gobernanza: los resultados Vector V8 permanecen internos de CTH. No liberar informes al cliente; los resultados no alimentan el 9-box, TalentIntelligence ni benchmarks.' })}
        </span>
      </div>

      <Card className="p-5 shadow-card space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <Label>{t('vectorv8.instrument', { defaultValue: 'Instrumento Vector V8' })}</Label>
            <Select value={selectedQ} onValueChange={setSelectedQ}>
              <SelectTrigger data-testid="vector-questionnaire-select">
                <SelectValue placeholder={t('vectorv8.pickInstrument', { defaultValue: 'Selecciona el instrumento…' })} />
              </SelectTrigger>
              <SelectContent>
                {(questionnaires || []).map((q) => (
                  <SelectItem key={q.questionnaire_id} value={q.questionnaire_id}>
                    {q.name} · v{q.version_number} · {q.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('vectorv8.from', { defaultValue: 'Desde' })}</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="vector-date-from" />
          </div>
          <div>
            <Label>{t('vectorv8.to', { defaultValue: 'Hasta' })}</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="vector-date-to" />
          </div>
        </div>

        {questionnaires && questionnaires.length === 0 && (
          <p className="text-sm text-muted-foreground py-4" data-testid="vector-empty">
            {t('vectorv8.none', { defaultValue: 'Ningún instrumento Vector V8 detectado todavía. Importa el archivo Vector V8 (94 ítems con columnas dimension + item_id) desde Assessment Studio → Importar.' })}
          </p>
        )}

        {val && (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs space-y-1" data-testid="vector-validation-card">
            <div className="flex items-center gap-2 font-semibold">
              {val.valid
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                : <AlertTriangle className="h-4 w-4 text-amber-600" />}
              {t('vectorv8.validation', { defaultValue: 'Validación estructural' })}:{' '}
              {val.item_count} ítems · {val.found_groupings}/{val.expected_groupings} agrupaciones dimensionales · {val.items_with_source_id} con item_id de origen
            </div>
            {val.missing_groupings?.length > 0 && (
              <p className="text-amber-700">Agrupaciones faltantes: {val.missing_groupings.join(', ')}</p>
            )}
            {Object.keys(val.unexpected_dimension_tags || {}).length > 0 && (
              <p className="text-amber-700">Dimensiones no reconocidas: {Object.keys(val.unexpected_dimension_tags).join(' · ')}</p>
            )}
            {val.duplicate_source_ids?.length > 0 && (
              <p className="text-red-700">item_id duplicados: {val.duplicate_source_ids.join(', ')}</p>
            )}
            {val.reverse_scored_items?.length > 0 && (
              <p className="text-red-700">Ítems con reverse_scored (no autorizado en V1.1): {val.reverse_scored_items.join(', ')}</p>
            )}
          </div>
        )}

        {selectedQ && (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-sm">
              {t('vectorv8.completed', { defaultValue: 'Evaluaciones completadas' })} ({assignments.length})
            </h2>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy === 'batch' || assignments.length === 0}
              onClick={() => {
                const params = new URLSearchParams({ questionnaire_id: selectedQ, status: 'submitted' });
                if (dateFrom) params.set('date_from', dateFrom);
                if (dateTo) params.set('date_to', dateTo);
                doDownload('batch', `/admin/vector-v8/export/batch?${params}`, 'vector_v8_batch.xlsx');
              }}
              data-testid="vector-batch-export-btn"
            >
              {busy === 'batch' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
              {t('vectorv8.batchExport', { defaultValue: 'Exportación batch (Long + Wide)' })}
            </Button>
          </div>
        )}

        {loadingAsn ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading', { defaultValue: 'Cargando…' })}
          </div>
        ) : selectedQ && assignments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3" data-testid="vector-no-assignments">
            {t('vectorv8.noCompleted', { defaultValue: 'Aún no hay evaluaciones Vector V8 completadas con los filtros actuales.' })}
          </p>
        ) : assignments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vector-assignments-table">
              <thead>
                <tr className="text-xs text-muted-foreground border-b border-border">
                  <th className="text-left py-2 pr-2">{t('vectorv8.participant', { defaultValue: 'Participante' })}</th>
                  <th className="text-left py-2 px-2">{t('vectorv8.company', { defaultValue: 'Empresa' })}</th>
                  <th className="text-left py-2 px-2">{t('vectorv8.submittedAt', { defaultValue: 'Completada' })}</th>
                  <th className="text-right py-2 pl-2">{t('vectorv8.actions', { defaultValue: 'Acciones' })}</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.assignment_id} className="border-b border-border/50" data-testid={`vector-asn-row-${a.assignment_id}`}>
                    <td className="py-2 pr-2 font-medium">{a.participant_name || a.participant_id}</td>
                    <td className="py-2 px-2">{a.company_name}</td>
                    <td className="py-2 px-2 text-muted-foreground">{fmtDate(a.submitted_at)}</td>
                    <td className="py-2 pl-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                                disabled={busy === `comp-${a.assignment_id}`}
                                onClick={() => openComputed(a.assignment_id)}
                                data-testid={`vector-computed-btn-${a.assignment_id}`}>
                          <Eye className="h-3 w-3" /> {t('vectorv8.viewComputed', { defaultValue: 'Cálculo' })}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                                disabled={busy === `x-${a.assignment_id}`}
                                onClick={() => doDownload(`x-${a.assignment_id}`, `/admin/vector-v8/assignments/${a.assignment_id}/export?format=xlsx`, 'vector_v8.xlsx')}
                                data-testid={`vector-xlsx-btn-${a.assignment_id}`}>
                          <Download className="h-3 w-3" /> XLSX
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                                disabled={busy === `c-${a.assignment_id}`}
                                onClick={() => doDownload(`c-${a.assignment_id}`, `/admin/vector-v8/assignments/${a.assignment_id}/export?format=csv`, 'vector_v8.csv')}
                                data-testid={`vector-csv-btn-${a.assignment_id}`}>
                          <FileText className="h-3 w-3" /> CSV
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={computedOpen} onOpenChange={setComputedOpen}>
        <DialogContent className="w-[90vw] max-w-[1400px] sm:max-w-none max-h-[88vh] h-auto overflow-y-auto" data-testid="vector-computed-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('vectorv8.computedTitleV2', { defaultValue: 'Resultados de Vector V8' })} — {computed?.participant?.name}
            </DialogTitle>
          </DialogHeader>
          {computed && (
            <div className="space-y-5 text-sm">
              <div className="text-xs text-muted-foreground">
                {computed.questionnaire?.name} · v{computed.questionnaire?.version_number} · {computed.answered}/{computed.total_items} ítems respondidos · {fmtDate(computed.submitted_at)}
                <span className="block mt-0.5">
                  Estado del informe: <span className="font-medium">{computed.report_status_label || computed.report_status || 'Sin informe — resultados internos CTH'}</span>
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-border px-3 py-2" data-testid="vector-idp-kpi">
                  <p className="text-xs text-muted-foreground">IDP (desplazamiento Base → Presión)</p>
                  <p className="text-2xl font-bold tabular-nums">{computed.idp ?? '—'}</p>
                  {computed.idp_note && <p className="text-[11px] text-amber-700">{computed.idp_note}</p>}
                </div>
                <div className="rounded-md border border-border px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('vectorv8.largestIncrease', { defaultValue: 'Mayor intensificación' })}</p>
                  <p className="font-semibold">{computed.largest_increase ? `${computed.largest_increase.name_es || computed.largest_increase.name} (+${computed.largest_increase.delta})` : '—'}</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('vectorv8.largestDecrease', { defaultValue: 'Mayor reducción' })}</p>
                  <p className="font-semibold">{computed.largest_decrease ? `${computed.largest_decrease.name_es || computed.largest_decrease.name} (${computed.largest_decrease.delta})` : '—'}</p>
                </div>
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <ComputedTable title="Motores (5)" rows={computed.motors} />
                <ComputedTable title="Recursos (8)" rows={computed.resources} />
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Saturación (SAT)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {computed.saturation.map((s) => (
                    <div key={s.code} className="rounded-md border border-border px-2.5 py-1.5">
                      <p className="text-xs text-muted-foreground truncate" title={s.name_es || s.name}>{s.name_es || s.name}</p>
                      <p className="font-semibold tabular-nums">{s.sat ?? '—'}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {t('vectorv8.noInterpretationV2', { defaultValue: 'Resultados numéricos calculados a partir de las respuestas. La interpretación y el informe premium se generan externamente.' })}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
